import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Section = styled.section`
  padding: 8rem 2rem;
  background: linear-gradient(
    180deg,
    #0a0a0a 0%,
    #0a0a0a 35%,
    #0a0a0a 50%,
    rgba(10, 10, 10, 1) 75%,
    #000000ff 100%
  );
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const Title = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ffffffff, #000000ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4rem;
  letter-spacing: -0.5px;
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  position: relative;
`;

const StepCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 122, 255, 0.15);
  border-radius: 28px;
  padding: 2.2rem;
  backdrop-filter: blur(14px);
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;

  &::before {
    content: '${props => props.number}';
    position: absolute;
    top: -15px;
    right: -15px;
    font-size: 8rem;
    font-weight: 900;
    color: rgba(0, 245, 255, 0.06);
    pointer-events: none;
    z-index: 0;
    line-height: 1;
  }

  &:hover {
    transform: translateY(-10px);
    border-color: #00f5ff;
    box-shadow: 0 25px 50px rgba(0, 245, 255, 0.12);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 4px;
    background: linear-gradient(90deg, #0072ff, #00f5ff);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.5s ease;
  }

  &:hover::after {
    transform: scaleX(1);
  }
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1.8rem;
  background: rgba(0, 245, 255, 0.08);
  border: 1.5px solid rgba(0, 245, 255, 0.25);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;

  svg {
    width: 32px;
    height: 32px;
    stroke: #00f5ff;
    stroke-width: 1.8;
  }
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 1rem;
  position: relative;
  z-index: 1;
`;

const StepDesc = styled.p`
  color: #cccccc;
  line-height: 1.7;
  font-size: 1.08rem;
  margin: 0;
  position: relative;
  z-index: 1;
`;

const HowItWorks = () => {
  const { t } = useTranslation();

  const steps = [
    {
      number: "1",
      title: t("how_step1_title"),
      desc: t("how_step1_desc"),
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
          <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8"/>
        </svg>
      )
    },
    {
      number: "2",
      title: t("how_step2_title"),
      desc: t("how_step2_desc"),
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 2v2m0 16v2m8-10h2M4 12H2m15.364 5.364l1.414 1.414M6.222 6.222L4.808 4.808m12.728 0l1.414 1.414M6.222 17.778l-1.414 1.414" stroke="currentColor" strokeWidth="1.8"/>
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/>
        </svg>
      )
    },
    {
      number: "3",
      title: t("how_step3_title"),
      desc: t("how_step3_desc"),
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    }
  ];

  return (
    <Section>
      <Container>
        <Title
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {t('how_title')}
        </Title>

        <Steps>
          {steps.map((step, i) => (
            <StepCard
              key={i}
              number={step.number}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2, duration: 0.7, ease: "easeOut" }}
            >
              <IconWrapper>{step.icon}</IconWrapper>
              <StepTitle>{step.title}</StepTitle>
              <StepDesc>{step.desc}</StepDesc>
            </StepCard>
          ))}
        </Steps>
      </Container>
    </Section>
  );
};

export default HowItWorks;